import { getAuth } from 'firebase/auth';
import { collection, doc, getDoc, getDocs, serverTimestamp, writeBatch } from 'firebase/firestore';

import { calculatePricing, DEFAULT_CALCULATOR_PARAMS } from '@/features/Calculator/utils';
import {
  fetchCollaboratorsSalary,
  type CollaboratorSalaryData,
  type SalaryHistoryEntry,
} from '@/features/Collaborators/Services/salarySubcollectionService';
import { db } from '@/config/firebase';
import { listHealthPlanImportsByCompetence } from './healthPlanImportsService';
import { listHealthPromotionImportsByCompetence } from './healthPromotionImportsService';
import { listFoodBenefitImportsByCompetence } from './foodBenefitImportsService';
import { listFuneralAssistanceImportsByCompetence } from './funeralAssistanceImportsService';
import { listPetPlanImportsByCompetence } from './petPlanImportsService';
import { listPayrollImports } from './payrollImportsService';
import type { PayrollImportDocument } from './payrollTypes';
import type { PayrollItem } from './payrollTypes';

type PayrollBreakdownItem = { code: string; description: string; total: number };

type CollaboratorAggregate = {
  collaboratorId: string;
  name: string;
  avatar?: string | null;
  payrollEarningsTotal: number;
  payrollDeductionsTotal: number;
  payrollNet: number;
  payrollImportId: string | null;
};

const EXPENSES_COLLECTION = 'expenses';
const CONSOLIDATION_SPREAD_RATE = 0.17;
const CHARGES_RATE = 0.266;

const PROVISION_RATES = {
  thirteenth: 0.0833,
  vacation: 0.0833,
  constitutional: 0.0278,
  fgtsVacation: 0.0089,
  fgtsThirteenth: 0.0066,
  fgtsPenalty: 0.032,
};

type CollaboratorCalculatorSnapshot = {
  input: {
    salary: number;
    salaryType: string | null;
    spreadRate: number;
    clt: boolean;
    changeDate: string | null;
  };
  result: {
    saleValue: number;
    saleHourlyRate: number;
    spreadValue: number;
    spreadPercentage: number;
    monthlyCost: number;
    hourlyCost: number;
    taxes: number;
    taxesPerHour: number;
    salaryPerHour: number;
    benefitsCost: number;
    laborObligationsCost: number;
    terminationCost: number;
    benefitsBreakdown?: {
      mealAllowance: number;
      healthPlan: number;
      transportation: number;
      financialEducation: number;
    };
    laborObligationsBreakdown?: {
      profitSharing: number;
      inss: number;
      fgts: number;
      vacation: number;
      thirteenth: number;
      notice: number;
      sickLeave: number;
      paternityLeave: number;
      terminationFine: number;
    };
  };
  calculatedAt: ReturnType<typeof serverTimestamp>;
};

function getLatestSalaryEntry(salaryData?: CollaboratorSalaryData): SalaryHistoryEntry | null {
  if (!salaryData) return null;
  const candidates: SalaryHistoryEntry[] = [];

  if (Array.isArray(salaryData.history)) {
    candidates.push(...salaryData.history);
  }
  if (salaryData.currentEntry) {
    candidates.push(salaryData.currentEntry);
  }
  if (salaryData.upcomingEntry) {
    candidates.push(salaryData.upcomingEntry);
  }

  if (candidates.length === 0) return null;

  const getTime = (entry: SalaryHistoryEntry) => {
    const time = new Date(entry.changeDate).getTime();
    return Number.isNaN(time) ? 0 : time;
  };

  return candidates.sort((a, b) => getTime(b) - getTime(a))[0] ?? null;
}

function runCalculatorForSalary(
  salaryEntry: SalaryHistoryEntry | null,
  collaboratorId: string,
): CollaboratorCalculatorSnapshot | null {
  if (!salaryEntry || typeof salaryEntry.amount !== 'number' || salaryEntry.amount <= 0) {
    return null;
  }

  const isClt = (salaryEntry.salaryType ?? '').toUpperCase() !== 'PJ';

  try {
    const calculation = calculatePricing({
      bySalary: true,
      bySaleHourlyRate: false,
      bySpread: true,
      salary: salaryEntry.amount,
      spreadRate: CONSOLIDATION_SPREAD_RATE,
      clt: isClt,
    });

    const salaryValue = calculation.salary;
    const monthlyHours =
      calculation.salaryPerHour && calculation.salaryPerHour > 0
        ? calculation.salary / calculation.salaryPerHour
        : DEFAULT_CALCULATOR_PARAMS.monthlyHours;

    const benefitMeal = calculation.mealAllowanceCost ?? (isClt ? DEFAULT_CALCULATOR_PARAMS.mealAllowance : 0);
    const benefitHealth = calculation.healthPlanCost ?? (isClt ? DEFAULT_CALCULATOR_PARAMS.healthPlan : 0);
    const benefitTransport =
      calculation.transportationCost ?? (isClt ? DEFAULT_CALCULATOR_PARAMS.transportation : 0);
    const benefitFinancial = calculation.financialEducationCost ?? DEFAULT_CALCULATOR_PARAMS.financialEducation;
    const computedBenefits = benefitMeal + benefitHealth + benefitTransport + benefitFinancial;
    const benefitsCost = calculation.benefitsCost ?? computedBenefits;

    const computedLabor = calculation.inssCost ?? 0;
    const laborObligationsCost = calculation.laborObligationsCost ?? computedLabor;

    const computedMonthly = salaryValue + laborObligationsCost + benefitsCost;
    const monthlyCost = calculation.monthlyCost ?? computedMonthly;
    const hourlyCost = calculation.hourlyCost ?? (monthlyHours ? monthlyCost / monthlyHours : 0);

    return {
      input: {
        salary: salaryEntry.amount,
        salaryType: salaryEntry.salaryType ?? null,
        spreadRate: CONSOLIDATION_SPREAD_RATE,
        clt: isClt,
        changeDate: salaryEntry.changeDate ?? null,
      },
      result: {
        saleValue: calculation.saleValue,
        saleHourlyRate: calculation.saleHourlyRate,
        spreadValue: calculation.spreadValue,
        spreadPercentage: calculation.spreadPercentage,
        monthlyCost,
        hourlyCost,
        taxes: calculation.taxes,
        taxesPerHour: calculation.taxesPerHour,
        salaryPerHour: calculation.salaryPerHour,
        benefitsCost,
        laborObligationsCost,
        terminationCost: calculation.terminationCost,
        benefitsBreakdown: {
          mealAllowance: calculation.mealAllowanceCost ?? 0,
          healthPlan: calculation.healthPlanCost ?? 0,
          transportation: calculation.transportationCost ?? 0,
          financialEducation: calculation.financialEducationCost ?? 0,
        },
        laborObligationsBreakdown: {
          profitSharing: calculation.profitSharingCost ?? 0,
          inss: calculation.inssCost ?? 0,
          fgts: calculation.fgtsCost ?? 0,
          vacation: calculation.vacationCost ?? 0,
          thirteenth: calculation.thirteenthSalaryCost ?? 0,
          notice: calculation.noticeCost ?? 0,
          sickLeave: calculation.sickLeaveCost ?? 0,
          paternityLeave: calculation.paternityLeaveCost ?? 0,
          terminationFine: calculation.terminationFineCost ?? 0,
        },
      },
      calculatedAt: serverTimestamp(),
    };
  } catch (error) {
    console.error(
      `[Expenses] Falha ao executar calculadora para colaborador ${collaboratorId}`,
      error,
    );
    return null;
  }
}

function aggregatePayroll(imports: PayrollImportDocument[]) {
  const payrollImportIds: string[] = [];
  let earningsTotal = 0;
  let deductionsTotal = 0;
  const earningsByCode = new Map<string, PayrollBreakdownItem>();
  const deductionsByCode = new Map<string, PayrollBreakdownItem>();
  const collaborators = new Map<string, CollaboratorAggregate>();

  const accumulateByCode = (
    target: Map<string, PayrollBreakdownItem>,
    source?: Record<string, { total: number; description: string }>,
  ) => {
    if (!source) return;
    Object.entries(source).forEach(([code, payload]) => {
      const current = target.get(code) ?? { code, description: payload.description ?? '', total: 0 };
      current.total += typeof payload.total === 'number' ? payload.total : 0;
      if (!current.description && payload.description) current.description = payload.description;
      target.set(code, current);
    });
  };

  imports.forEach((importDoc) => {
    payrollImportIds.push(importDoc.id);
    const earningsFromTotals = importDoc.totals?.earning?.total ?? 0;
    const deductionsFromTotals = importDoc.totals?.deduction?.total ?? 0;

    if (earningsFromTotals || deductionsFromTotals) {
      earningsTotal += earningsFromTotals;
      deductionsTotal += deductionsFromTotals;
    } else {
      // Fallback: sum collaborator summaries
      importDoc.collaborators.forEach((col) => {
        earningsTotal += col.earningsTotal ?? 0;
        deductionsTotal += col.deductionsTotal ?? 0;
      });
    }

    accumulateByCode(earningsByCode, importDoc.totals?.earning?.byCode);
    accumulateByCode(deductionsByCode, importDoc.totals?.deduction?.byCode);

    importDoc.collaborators.forEach((col) => {
      if (!col.collaboratorId) return;
      const existing = collaborators.get(col.collaboratorId);
      const next: CollaboratorAggregate = existing ?? {
        collaboratorId: col.collaboratorId,
        name: col.collaboratorName ?? 'Sem nome',
        avatar: null,
        payrollEarningsTotal: 0,
        payrollDeductionsTotal: 0,
        payrollNet: 0,
        payrollImportId: null,
      };
      next.payrollEarningsTotal += col.earningsTotal ?? 0;
      next.payrollDeductionsTotal += col.deductionsTotal ?? 0;
      next.payrollNet = next.payrollEarningsTotal - next.payrollDeductionsTotal;
      if (!next.payrollImportId) {
        next.payrollImportId = importDoc.id;
      }
      collaborators.set(col.collaboratorId, next);
    });
  });

  const earningsBreakdown = Array.from(earningsByCode.values());
  const deductionsBreakdown = Array.from(deductionsByCode.values());

  return {
    payrollImportIds,
    earningsTotal,
    deductionsTotal,
    net: earningsTotal - deductionsTotal,
    earningsBreakdown,
    deductionsBreakdown,
    collaborators,
  };
}

function sumNumber(value: number | null | undefined): number {
  return typeof value === 'number' && Number.isFinite(value) ? value : 0;
}

function buildProvisionsFromBaseSalary(baseSalary: number) {
  const thirteenth = baseSalary * PROVISION_RATES.thirteenth;
  const vacation = baseSalary * PROVISION_RATES.vacation;
  const constitutional = baseSalary * PROVISION_RATES.constitutional;
  const fgtsVacation = baseSalary * PROVISION_RATES.fgtsVacation;
  const fgtsThirteenth = baseSalary * PROVISION_RATES.fgtsThirteenth;
  const fgtsPenalty = baseSalary * PROVISION_RATES.fgtsPenalty;

  const total =
    thirteenth +
    vacation +
    constitutional +
    fgtsVacation +
    fgtsThirteenth +
    fgtsPenalty;

  return {
    thirteenth,
    vacation,
    constitutional,
    fgtsVacation,
    fgtsThirteenth,
    fgtsPenalty,
    total,
  };
}

function getPetPremiumTotal(importDoc: any): number {
  const fromRecords = Array.isArray(importDoc?.recordsPreview)
    ? importDoc.recordsPreview.reduce((sum: number, record: any) => {
        const premium = typeof record?.premium === 'number' ? record.premium : typeof record?.amount === 'number' ? record.amount : 0;
        return sum + premium;
      }, 0)
    : 0;
  const totalAmount = typeof importDoc?.totals?.amount === 'number' ? importDoc.totals.amount : 0;
  return fromRecords > 0 ? fromRecords : totalAmount;
}

type PayrollRecordDetail = {
  earningsBreakdown: PayrollBreakdownItem[];
  deductionsBreakdown: PayrollBreakdownItem[];
  baseSalary?: number | null;
  fgtsValue?: number | null;
  net?: number | null;
};

async function loadPayrollRecordDetails(imports: PayrollImportDocument[]): Promise<Map<string, PayrollRecordDetail>> {
  const map = new Map<string, PayrollRecordDetail>();

  await Promise.all(
    imports.map(async (importDoc) => {
      const recordsRef = collection(db, 'payroll_imports', importDoc.id, 'records');
      const snap = await getDocs(recordsRef);
      snap.forEach((docSnap) => {
        const data: any = docSnap.data();
        const collaboratorId = data?.collaboratorId;
        const payrollRecord = data?.payrollRecord;
        if (!collaboratorId || !payrollRecord) return;
        const items: PayrollItem[] = Array.isArray(payrollRecord.items) ? payrollRecord.items : [];
        const earningsBreakdown: PayrollBreakdownItem[] = [];
        const deductionsBreakdown: PayrollBreakdownItem[] = [];
        items.forEach((item) => {
          if (item?.pdType === 'earning') {
            const existing = earningsBreakdown.find((b) => b.code === item.code);
            if (existing) {
              existing.total += item.amount ?? 0;
            } else {
              earningsBreakdown.push({
                code: item.code,
                description: item.description,
                total: item.amount ?? 0,
              });
            }
          }
          if (item?.pdType === 'deduction') {
            const existing = deductionsBreakdown.find((b) => b.code === item.code);
            if (existing) {
              existing.total += item.amount ?? 0;
            } else {
              deductionsBreakdown.push({
                code: item.code,
                description: item.description,
                total: item.amount ?? 0,
              });
            }
          }
        });

        map.set(collaboratorId, {
          earningsBreakdown,
          deductionsBreakdown,
          baseSalary: payrollRecord?.baseSalary ?? null,
          fgtsValue: payrollRecord?.totals?.fgtsValue ?? payrollRecord?.totals?.baseFgts ?? null,
          net: payrollRecord?.totals?.netPay ?? null,
        });
      });
    }),
  );

  return map;
}

export async function consolidateExpensesByCompetence(competence: string): Promise<void> {
  if (!competence) {
    throw new Error('Competência inválida para consolidação.');
  }

  const auth = getAuth();
  const currentUser = auth.currentUser;
  if (!currentUser) {
    throw new Error('É necessário estar autenticado para consolidar despesas.');
  }

  const [payrollImports, healthPlan, healthPromotion, foodBenefit, funeralAssistance, petPlan] = await Promise.all([
    listPayrollImports(),
    listHealthPlanImportsByCompetence(competence),
    listHealthPromotionImportsByCompetence(competence),
    listFoodBenefitImportsByCompetence(competence),
    listFuneralAssistanceImportsByCompetence(competence),
    listPetPlanImportsByCompetence(competence),
  ]);

  const payrollForCompetence = payrollImports.filter((item) => (item.competence ?? '') === competence);
  const payrollData = aggregatePayroll(payrollForCompetence);
  const payrollCollaboratorIds = Array.from(payrollData.collaborators.keys());
  const payrollDetailsByCollaborator = await loadPayrollRecordDetails(payrollForCompetence);
  const salaryDataByCollaborator =
    payrollCollaboratorIds.length > 0 ? await fetchCollaboratorsSalary(payrollCollaboratorIds) : {};

  const benefitDeductions8111 = payrollImports
    .filter((item) => (item.competence ?? '') === competence)
    .reduce((acc, importDoc) => {
      const byCode = importDoc.totals?.deduction?.byCode ?? {};
      const deduction = byCode['8111']?.total ?? 0;
      return acc + sumNumber(deduction);
    }, 0);

  const benefitDeductionsFood = payrollForCompetence.reduce((acc, importDoc) => {
    const byCode = importDoc.totals?.deduction?.byCode ?? importDoc.totals?.deductions?.byCode ?? {};
    const d202 = byCode['202']?.total ?? 0;
    const d233 = byCode['233']?.total ?? 0;
    return acc + sumNumber(d202) + sumNumber(d233);
  }, 0);

  const benefitDeductionsFuneral = payrollForCompetence.reduce((acc, importDoc) => {
    const byCode = importDoc.totals?.deduction?.byCode ?? importDoc.totals?.deductions?.byCode ?? {};
    const d243 = byCode['243']?.total ?? 0;
    return acc + sumNumber(d243);
  }, 0);

  const benefitsTotals = {
    healthPlan: Math.max(
      healthPlan.reduce((sum, doc) => sum + sumNumber(doc.totals?.premiumHealth), 0) - benefitDeductions8111,
      0,
    ),
    healthPromotion: healthPromotion.reduce((sum, doc) => sum + sumNumber(doc.totals?.amount), 0),
    foodBenefit: Math.max(
      foodBenefit.reduce((sum, doc) => sum + sumNumber(doc.totals?.amount), 0) - benefitDeductionsFood,
      0,
    ),
    funeralAssistance: Math.max(
      funeralAssistance.reduce((sum, doc) => sum + sumNumber(doc.totals?.amount), 0) - benefitDeductionsFuneral,
      0,
    ),
    petPlan: petPlan.reduce((sum, doc) => sum + getPetPremiumTotal(doc), 0),
  };

  const benefitsTotal =
    benefitsTotals.healthPlan +
    benefitsTotals.healthPromotion +
    benefitsTotals.foodBenefit +
    benefitsTotals.funeralAssistance +
    benefitsTotals.petPlan;

  const collaboratorCount = payrollCollaboratorIds.length || 1;

  const benefitsAverages = {
    healthPlan: benefitsTotals.healthPlan / collaboratorCount,
    healthPromotion: benefitsTotals.healthPromotion / collaboratorCount,
    foodBenefit: benefitsTotals.foodBenefit / collaboratorCount,
    funeralAssistance: benefitsTotals.funeralAssistance / collaboratorCount,
    petPlan: benefitsTotals.petPlan / collaboratorCount,
    total: benefitsTotal / collaboratorCount,
  };

  const proventsAverage = payrollData.earningsTotal / collaboratorCount;
  let avgBaseSalary = 0;
  let avgFgts = 0;
  payrollDetailsByCollaborator.forEach((details) => {
    avgBaseSalary += sumNumber(details.baseSalary);
    avgFgts += sumNumber(details.fgtsValue);
  });
  avgBaseSalary = avgBaseSalary / collaboratorCount;
  avgFgts = avgFgts / collaboratorCount;
  if (!avgFgts) {
    avgFgts = proventsAverage * DEFAULT_CALCULATOR_PARAMS.fgtsRate;
  }
  const avgCharges = avgBaseSalary * CHARGES_RATE;
  const avgProvisions = {
    thirteenth: proventsAverage * PROVISION_RATES.thirteenth,
    vacation: proventsAverage * PROVISION_RATES.vacation,
    constitutional: proventsAverage * PROVISION_RATES.constitutional,
    fgtsVacation: proventsAverage * PROVISION_RATES.fgtsVacation,
    fgtsThirteenth: proventsAverage * PROVISION_RATES.fgtsThirteenth,
    fgtsPenalty: proventsAverage * PROVISION_RATES.fgtsPenalty,
  };
  const avgProvisionsTotal =
    avgProvisions.thirteenth +
    avgProvisions.vacation +
    avgProvisions.constitutional +
    avgProvisions.fgtsVacation +
    avgProvisions.fgtsThirteenth +
    avgProvisions.fgtsPenalty;
  const avgEncumbrancesSubtotal = avgBaseSalary + avgFgts + avgCharges;
  const avgBenefitsSubtotal = benefitsAverages.total;
  const avgTotal = avgBenefitsSubtotal + avgEncumbrancesSubtotal + avgProvisionsTotal;
  const avgHourly = avgTotal / DEFAULT_CALCULATOR_PARAMS.monthlyHours;

  const averagesSnapshot = {
    collaboratorCount,
    benefits: {
      ...benefitsAverages,
      subtotal: benefitsAverages.total,
    },
    payroll: {
      baseSalary: avgBaseSalary,
      fgts: avgFgts,
      charges: avgCharges,
      provents: proventsAverage,
      encumbrancesSubtotal: avgEncumbrancesSubtotal,
      hourlyCost: avgHourly,
    },
    provisions: {
      ...avgProvisions,
      total: avgProvisionsTotal,
    },
    totals: {
      total: avgTotal,
      hourlyCost: avgHourly,
      encumbrancesSubtotal: avgEncumbrancesSubtotal,
      benefitsSubtotal: avgBenefitsSubtotal,
    },
  };

  const hasAnyData =
    payrollData.payrollImportIds.length > 0 ||
    healthPlan.length > 0 ||
    healthPromotion.length > 0 ||
    foodBenefit.length > 0 ||
    funeralAssistance.length > 0 ||
    petPlan.length > 0;

  if (!hasAnyData) {
    throw new Error('Nenhum dado encontrado para esta competência.');
  }

  const expenseDocRef = doc(db, EXPENSES_COLLECTION, competence);
  const collaboratorCollectionRef = collection(expenseDocRef, 'collaborators');

  const baseExpenseData = {
    competence,
    status: 'consolidated',
    consolidatedAt: serverTimestamp(),
    consolidatedBy: currentUser.uid,
    version: 1,
    totals: {
      payroll: {
        earningsTotal: payrollData.earningsTotal,
        deductionsTotal: payrollData.deductionsTotal,
        net: payrollData.net,
        earningsBreakdown: payrollData.earningsBreakdown,
        deductionsBreakdown: payrollData.deductionsBreakdown,
      },
      benefits: {
        ...benefitsTotals,
        total: benefitsTotal,
      },
      averages: averagesSnapshot,
      grandTotal: payrollData.net + benefitsTotal,
    },
    sources: {
      payrollImportIds: payrollData.payrollImportIds,
      benefitImportIds: {
        healthPlan: healthPlan.map((item) => item.id),
        healthPromotion: healthPromotion.map((item) => item.id),
        foodBenefit: foodBenefit.map((item) => item.id),
        funeralAssistance: funeralAssistance.map((item) => item.id),
        petPlan: petPlan.map((item) => item.id),
      },
    },
    meta: {
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
    },
  };

  const operations: Array<(batch: ReturnType<typeof writeBatch>) => void> = [];
  operations.push((batch) => batch.set(expenseDocRef, baseExpenseData, { merge: true }));

  const collaboratorBenefits = new Map<
    string,
    {
      healthPlan: number;
      healthPromotion: number;
      foodBenefit: number;
      funeralAssistance: number;
      petPlan: number;
    }
  >();

  const addBenefit = (collaboratorId: string | null | undefined, key: keyof (typeof benefitsTotals)) => (amount: number) => {
    if (!collaboratorId || !Number.isFinite(amount) || amount === 0) return;
    const current = collaboratorBenefits.get(collaboratorId) ?? {
      healthPlan: 0,
      healthPromotion: 0,
      foodBenefit: 0,
      funeralAssistance: 0,
      petPlan: 0,
    };
    current[key] += amount;
    collaboratorBenefits.set(collaboratorId, current);
  };

  healthPlan.forEach((importDoc) => {
    if (!Array.isArray((importDoc as any).familiesPreview)) return;
    (importDoc as any).familiesPreview.forEach((family: any) => {
      const amount = sumNumber(family?.totalPremium);
      addBenefit(family?.collaboratorId, 'healthPlan')(amount);
    });
  });

  healthPromotion.forEach((importDoc) => {
    importDoc.recordsPreview?.forEach((record: any) => {
      const amount = sumNumber(record?.amount);
      addBenefit(record?.collaboratorId, 'healthPromotion')(amount);
    });
  });

  foodBenefit.forEach((importDoc) => {
    importDoc.recordsPreview?.forEach((record: any) => {
      const amount = sumNumber(record?.amount);
      addBenefit(record?.collaboratorId, 'foodBenefit')(amount);
    });
  });

  funeralAssistance.forEach((importDoc) => {
    importDoc.recordsPreview?.forEach((record: any) => {
      const amount = sumNumber(record?.amount);
      addBenefit(record?.collaboratorId, 'funeralAssistance')(amount);
    });
  });

  petPlan.forEach((importDoc) => {
    importDoc.recordsPreview?.forEach((record: any) => {
      const premium = sumNumber(record?.premium ?? record?.amount);
      addBenefit(record?.collaboratorId, 'petPlan')(premium);
    });
  });

  payrollData.collaborators.forEach((collaborator) => {
    const collaboratorDocRef = doc(collaboratorCollectionRef, collaborator.collaboratorId);
    const salaryData = salaryDataByCollaborator[collaborator.collaboratorId];
    const latestSalaryEntry = getLatestSalaryEntry(salaryData);
    const payrollDetails = payrollDetailsByCollaborator.get(collaborator.collaboratorId);
    const aggregatedBenefits = collaboratorBenefits.get(collaborator.collaboratorId) ?? {
      healthPlan: 0,
      healthPromotion: 0,
      foodBenefit: 0,
      funeralAssistance: 0,
      petPlan: 0,
    };
    const calculatorSnapshot = runCalculatorForSalary(latestSalaryEntry, collaborator.collaboratorId);
    const deduction8111 =
      payrollDetails?.deductionsBreakdown?.find((item) => item.code === '8111')?.total ?? 0;
    const deductionFood =
      (payrollDetails?.deductionsBreakdown?.find((item) => item.code === '202')?.total ?? 0) +
      (payrollDetails?.deductionsBreakdown?.find((item) => item.code === '233')?.total ?? 0);
    const deductionFuneral = payrollDetails?.deductionsBreakdown?.find((item) => item.code === '243')?.total ?? 0;
    const adjustedHealthPlan = Math.max(aggregatedBenefits.healthPlan - deduction8111, 0);
    const adjustedFoodBenefit = Math.max(aggregatedBenefits.foodBenefit - deductionFood, 0);
    const adjustedFuneral = Math.max(aggregatedBenefits.funeralAssistance - deductionFuneral, 0);
    const adjustedPet = aggregatedBenefits.petPlan;
    const benefitsCost =
      adjustedHealthPlan +
      aggregatedBenefits.healthPromotion +
      adjustedFoodBenefit +
      adjustedFuneral +
      adjustedPet;
    const baseSalaryRaw = payrollDetails?.baseSalary ?? latestSalaryEntry?.amount ?? null;
    const baseSalaryValue = sumNumber(baseSalaryRaw);
    const fgtsRaw = payrollDetails?.fgtsValue ?? null;
    const fgtsValue = sumNumber(fgtsRaw);
    const chargesValue = sumNumber(collaborator.payrollEarningsTotal) * CHARGES_RATE;
    const provisions = buildProvisionsFromBaseSalary(baseSalaryValue);
    const encumbrancesSubtotal = baseSalaryValue + fgtsValue + chargesValue;
    const total = benefitsCost + encumbrancesSubtotal + provisions.total;
    const hourlyCost = total / DEFAULT_CALCULATOR_PARAMS.monthlyHours;

    const calculatorTotals = calculatorSnapshot
      ? (() => {
          const laborBreakdown = calculatorSnapshot.result?.laborObligationsBreakdown;
          const calcSalary = calculatorSnapshot.input?.salary ?? null;
          const calcFgts = laborBreakdown?.fgts ?? null;
          const calcInss = laborBreakdown?.inss ?? null;
          const hasLaborBreakdown = Boolean(laborBreakdown);
          const calcEncumbrancesSubtotal =
            calcSalary !== null || calcFgts !== null || calcInss !== null
              ? sumNumber(calcSalary) + sumNumber(calcFgts) + sumNumber(calcInss)
              : null;
          const calcProvisionsTotal = hasLaborBreakdown
            ? sumNumber(laborBreakdown?.thirteenth) +
              sumNumber(laborBreakdown?.vacation) +
              sumNumber(laborBreakdown?.terminationFine) +
              sumNumber(laborBreakdown?.notice) +
              sumNumber(laborBreakdown?.sickLeave) +
              sumNumber(laborBreakdown?.paternityLeave) +
              sumNumber(laborBreakdown?.profitSharing)
            : null;
          const calcBenefitsSubtotal = calculatorSnapshot.result?.benefitsCost ?? null;
          const calcTotal =
            calcBenefitsSubtotal !== null ||
            calcEncumbrancesSubtotal !== null ||
            calcProvisionsTotal !== null
              ? sumNumber(calcBenefitsSubtotal) +
                sumNumber(calcEncumbrancesSubtotal) +
                sumNumber(calcProvisionsTotal)
              : null;

          return {
            benefitsSubtotal: calcBenefitsSubtotal,
            encumbrancesSubtotal: calcEncumbrancesSubtotal,
            provisionsTotal: calcProvisionsTotal,
            total: calcTotal,
            hourlyCost: calculatorSnapshot.result?.hourlyCost ?? null,
            salary: calcSalary,
          };
        })()
      : null;

    operations.push((batch) =>
      batch.set(
        collaboratorDocRef,
        {
          competence,
          collaboratorId: collaborator.collaboratorId,
          name: collaborator.name,
          avatar: collaborator.avatar ?? null,
          calculator: calculatorSnapshot ?? null,
          calculatorTotals,
          payroll: {
            earningsTotal: collaborator.payrollEarningsTotal,
            deductionsTotal: collaborator.payrollDeductionsTotal,
            net: payrollDetails?.net ?? collaborator.payrollNet,
            earningsBreakdown: payrollDetails?.earningsBreakdown ?? [],
            deductionsBreakdown: payrollDetails?.deductionsBreakdown ?? [],
            baseSalary: baseSalaryRaw ?? null,
            fgtsValue: fgtsRaw ?? null,
            encumbrancesSubtotal,
          },
          benefits: {
            healthPlan: adjustedHealthPlan,
            healthPromotion: aggregatedBenefits.healthPromotion,
            foodBenefit: adjustedFoodBenefit,
            funeralAssistance: adjustedFuneral,
            petPlan: adjustedPet,
            total: benefitsCost,
          },
          benefitsBreakdown: {
            healthPlan: adjustedHealthPlan,
            healthPromotion: aggregatedBenefits.healthPromotion,
            foodBenefit: adjustedFoodBenefit,
            funeralAssistance: adjustedFuneral,
            petPlan: adjustedPet,
          },
          averages: averagesSnapshot,
          computed: {
            charges: chargesValue,
            encumbrancesSubtotal,
            provisions,
            provisionsTotal: provisions.total,
            total,
            hourlyCost,
          },
          total,
          grandTotal: total,
          sources: {
            payrollImportId: collaborator.payrollImportId,
            benefitImportIds: {},
          },
          version: 1,
          createdAt: serverTimestamp(),
          updatedAt: serverTimestamp(),
        },
        { merge: true },
      ),
    );

    const collaboratorPrivateRef = doc(db, 'collaborators_private', collaborator.collaboratorId);
    operations.push((batch) =>
      batch.set(
        collaboratorPrivateRef,
        {
          lastExpensesCompetence: competence,
          lastExpensesPath: `${EXPENSES_COLLECTION}/${competence}/collaborators/${collaborator.collaboratorId}`,
          updatedAt: serverTimestamp(),
        },
        { merge: true },
      ),
    );
  });

  const chunkSize = 400;
  for (let i = 0; i < operations.length; i += chunkSize) {
    const chunk = operations.slice(i, i + chunkSize);
    const batch = writeBatch(db);
    chunk.forEach((op) => op(batch));
    await batch.commit();
  }
}

export async function removeExpensesConsolidation(competence: string): Promise<void> {
  if (!competence) {
    throw new Error('Competência inválida para remoção.');
  }

  const expenseDocRef = doc(db, EXPENSES_COLLECTION, competence);
  const collaboratorsRef = collection(expenseDocRef, 'collaborators');
  const collaboratorsSnap = await getDocs(collaboratorsRef);

  const collaboratorIds = collaboratorsSnap.docs.map((doc) => doc.id);

  const operations: Array<(batch: ReturnType<typeof writeBatch>) => void> = [];

  // Delete collaborators subcollection
  collaboratorsSnap.forEach((docSnap) => {
    operations.push((batch) => batch.delete(docSnap.ref));
  });

  // Clear collaborator_private pointers to this competence
  const collaboratorPrivateRefs = collaboratorIds.map((collaboratorId) =>
    doc(db, 'collaborators_private', collaboratorId),
  );
  const collaboratorPrivateSnaps = await Promise.all(collaboratorPrivateRefs.map((ref) => getDoc(ref)));

  collaboratorPrivateSnaps.forEach((snap, index) => {
    if (!snap.exists()) return;
    const currentCompetence = (snap.data() as any)?.lastExpensesCompetence;
    if (currentCompetence !== competence) return;
    operations.push((batch) =>
      batch.set(
        collaboratorPrivateRefs[index],
        {
          lastExpensesCompetence: null,
          lastExpensesPath: null,
          updatedAt: serverTimestamp(),
        },
        { merge: true },
      ),
    );
  });

  // Finally delete the expense document itself
  operations.push((batch) => batch.delete(expenseDocRef));

  const chunkSize = 400;
  for (let i = 0; i < operations.length; i += chunkSize) {
    const chunk = operations.slice(i, i + chunkSize);
    const batch = writeBatch(db);
    chunk.forEach((op) => op(batch));
    await batch.commit();
  }
}

export async function isCompetenceConsolidated(competence: string): Promise<boolean> {
  if (!competence) return false;
  const snapshot = await getDoc(doc(db, EXPENSES_COLLECTION, competence));
  return snapshot.exists();
}
